// ── Master Suite canvas geometry ─────────────────────────────────
// Single source of truth for the Alteryx-style workflow canvas layout.
// Both the node layer (WorkflowNode positions) and the connector layer
// (WorkflowConnector bezier paths) derive from THIS math, so the picture
// stays consistent with no DOM measurement / refs.

export const NODE_W = 190;
export const NODE_H = 72;
export const GAP_X = 70;
export const GAP_Y = 48;
export const PAD = 24;

/**
 * Orientation. "lr" = depth runs left→right (columns), the cross axis runs down.
 *
 * Left-to-right on purpose: ARC suites are chain-dominant with the occasional two or
 * three way branch, and the canvas is a wide, short panel. Putting chain length on the
 * viewport's long axis is what keeps a real suite legible — top-down would stack a
 * 15-step chain into a column far taller than the panel. It also keeps the cable
 * language (leave the right edge, arrive at the left) that the node cards were drawn for.
 *
 * Flipping to top-down is this constant plus pointFor/anchors below; nothing else in the
 * codebase knows which axis is which.
 */
export const ORIENTATION = "lr";

/** Pitch between adjacent slots on the cross axis — one node plus its gap. */
const CROSS_PITCH = NODE_H + GAP_Y;
/** Pitch between depths — one node plus its gap, along the depth axis. */
const DEPTH_PITCH = NODE_W + GAP_X;

/**
 * A laid-out node's top-left corner in pixels.
 *
 * `p` carries {depth, offset}: depth is distance from the root, offset is the tidy-tree
 * slot on the cross axis (fractional for a parent centred over its children). Positions
 * are COMPUTED from the tree — nothing is ever placed by hand or by list index.
 */
export const pointFor = (p) =>
  ORIENTATION === "lr"
    ? { x: PAD + p.depth * DEPTH_PITCH, y: PAD + p.offset * CROSS_PITCH }
    : { x: PAD + p.offset * DEPTH_PITCH, y: PAD + p.depth * CROSS_PITCH };

// Where cables leave and arrive. In "lr" that is the right and left mid-edge; the
// bezier control-point math downstream is unchanged either way.
export const rightAnchorAt = (p) => {
  const { x, y } = pointFor(p);
  return ORIENTATION === "lr"
    ? { x: x + NODE_W, y: y + NODE_H / 2 }
    : { x: x + NODE_W / 2, y: y + NODE_H };
};
export const leftAnchorAt = (p) => {
  const { x, y } = pointFor(p);
  return ORIENTATION === "lr"
    ? { x, y: y + NODE_H / 2 }
    : { x: x + NODE_W / 2, y };
};

// Midpoint of the cable from `a` to `b` — where the insert-between "+" button sits.
export const edgeMidpointAt = (a, b) => {
  const p = rightAnchorAt(a);
  const q = leftAnchorAt(b);
  return { x: (p.x + q.x) / 2, y: (p.y + q.y) / 2 };
};

/**
 * The box the laid-out tree occupies, from its extents rather than a node count.
 *
 * `maxOffset` is fractional (a parent sits at the mean of its children), so the cross
 * axis is measured from the pitch, not from a slot count.
 */
export const boundsFor = (depthCount, maxOffset = 0) => {
  const depthSpan =
    depthCount > 0 ? depthCount * NODE_W + (depthCount - 1) * GAP_X : NODE_W;
  const crossSpan = NODE_H + Math.max(0, maxOffset) * CROSS_PITCH;
  return ORIENTATION === "lr"
    ? { width: PAD * 2 + depthSpan, height: PAD * 2 + crossSpan }
    : { width: PAD * 2 + crossSpan, height: PAD * 2 + depthSpan };
};

// Cubic bezier with HORIZONTAL tangents at both ends — the cable look. Control points
// share the Y of their own anchor, so a cable leaves flat and arrives flat even when
// it crosses lanes.
/**
 * The cable curve between two arbitrary points.
 *
 * Split out from edgePathAt so the drag-preview cable and the real connectors are drawn
 * by the same maths and can never drift apart — the preview has to promise exactly the
 * cable the drop will produce.
 */
export const bezierBetween = (p, q) => {
  const dx = Math.max(40, (q.x - p.x) * 0.5);
  return `M ${p.x} ${p.y} C ${p.x + dx} ${p.y} ${q.x - dx} ${q.y} ${q.x} ${q.y}`;
};

export const edgePathAt = (a, b) =>
  bezierBetween(rightAnchorAt(a), leftAnchorAt(b));

/**
 * Assign a {depth, offset} to every node from its parent links — a tidy tree,
 * deterministic, with no DOM measurement.
 *
 * The algorithm is the classic two-rule tidy layout:
 *   1. every LEAF takes the next consecutive slot, in traversal order;
 *   2. every internal node sits at the MEAN of its children's slots, so a parent is
 *      centred over the span of its subtree.
 *
 * That is enough to guarantee no overlap, which is worth spelling out because it is not
 * obvious: sibling subtrees own disjoint, contiguous ranges of leaf slots, so for two
 * nodes at the same depth owning ranges [a,b] and [b+1,c] the midpoints differ by
 * (1 + c − a) / 2, which is at least 1 slot. The tightest case — two adjacent single
 * leaves — is exactly 1. No threading or collision pass is needed.
 *
 * This replaces the previous "lanes": a lane was claimed globally and never reused, so a
 * branch opened at depth 5 was pushed to row 3 even when rows 1 and 2 were empty there,
 * and parents were never centred over their children.
 *
 * A node whose parentId matches nothing is treated as a root rather than dropped — a
 * step must never vanish from the canvas because of a stale link. Nodes with no parent
 * links at all (every suite built before fan-out) come out as one straight chain.
 *
 * @returns {{nodes: Array, depthCount: number, maxOffset: number, edges: Array}}
 */
export function layoutTree(nodes = []) {
  if (!nodes.length)
    return { nodes: [], depthCount: 0, maxOffset: 0, edges: [] };

  // name → EVERY row carrying that name. Names are NOT unique while a step is being typed,
  // and a single-value map was last-write-wins: a duplicate silently stole the key, so
  // children attached to the wrong node and a step whose name matched its parent's became
  // its own parent and dropped out of the tree as a disconnected root at depth 0. That is
  // what left an orphan on the canvas after a rejected add — the add changed nothing; the
  // half-typed name did.
  const byRef = new Map();
  nodes.forEach((nd) => {
    if (nd.ref == null || nd.ref === "") return;
    if (!byRef.has(nd.ref)) byRef.set(nd.ref, []);
    byRef.get(nd.ref).push(nd);
  });

  // Resolve a name to a row that is NOT this node. With a duplicate, "my parent is B" can
  // only sensibly mean the OTHER B. First match, so the picture stays put while a name is
  // mid-edit instead of rearranging on every keystroke.
  const rowFor = (name, self) =>
    (byRef.get(name) || []).find((r) => r.key !== self.key) || null;

  const parentOf = (nd) => {
    const p = nd.parentId;
    if (p == null || p === "") return null;
    // No OTHER row owns that name — a stale link, or a step naming only itself. Root.
    return rowFor(p, nd) ? p : null;
  };

  const children = new Map();
  const roots = [];
  nodes.forEach((nd) => {
    const p = parentOf(nd);
    if (p === null) roots.push(nd);
    else {
      if (!children.has(p)) children.set(p, []);
      children.get(p).push(nd);
    }
  });

  // No parent links anywhere → the legacy linear suite. Chain it so it renders as
  // one line rather than N disconnected roots.
  const noLinks = roots.length === nodes.length;

  const placed = [];
  const pos = new Map();
  let nextLeafSlot = 0;

  // Post-order: a node's own offset needs its children's, so descend first.
  //
  // `seen` makes this total. Children are keyed by NAME, and names are only unique once
  // the user has finished typing — so a duplicate can briefly make one node another's
  // child AND its parent. Without the guard that recursed until the stack blew, which
  // unmounted the whole builder and lost unsaved steps. A layout must never be able to
  // throw: it runs on every keystroke, on data that is mid-edit by definition.
  const seen = new Set();
  const visit = (nd, depth) => {
    if (seen.has(nd.key)) return null; // already placed — a name cycle brought us back
    seen.add(nd.key);
    const kids = (children.get(nd.ref) || []).filter((k) => !seen.has(k.key));
    let offset;
    const kidOffsets = kids
      .map((kid) => visit(kid, depth + 1))
      .filter((o) => o != null);
    if (kidOffsets.length === 0) {
      offset = nextLeafSlot;
      nextLeafSlot += 1;
    } else {
      offset = (kidOffsets[0] + kidOffsets[kidOffsets.length - 1]) / 2;
    }
    const laid = { ...nd, depth, offset };
    placed.push(laid);
    pos.set(nd.key, laid);
    return offset;
  };

  if (noLinks) {
    // Legacy suite: no links at all, so it IS the chain. One straight line.
    nodes.forEach((nd, i) => {
      const laid = { ...nd, depth: i, offset: 0 };
      placed.push(laid);
      pos.set(nd.key, laid);
    });
  } else {
    // Roots are laid out as if they were children of one virtual super-root, so
    // independent trees sit side by side instead of overlapping at offset 0.
    roots.forEach((r) => visit(r, 0));
    // Anything the walk could not reach — a node trapped in a name cycle — is placed as
    // its own root. A step must never silently vanish from the canvas just because its
    // links are momentarily nonsense.
    nodes.forEach((nd) => {
      if (!seen.has(nd.key)) visit(nd, 0);
    });
  }

  // Parent → child cables. For the legacy chain there are no links, so pair each
  // node with the next one, reproducing the old i → i+1 picture exactly.
  const edges = [];
  if (noLinks) {
    for (let i = 0; i < placed.length - 1; i += 1) {
      edges.push({ from: placed[i], to: placed[i + 1] });
    }
  } else {
    placed.forEach((nd) => {
      const p = parentOf(nd);
      if (p == null) return;
      // rowFor, not byRef.get — byRef holds a LIST per name now, and the cable must land on
      // the same row the layout chose as this node's parent or the picture contradicts itself.
      const parentRow = rowFor(p, nd);
      const parent = parentRow && pos.get(parentRow.key);
      if (parent) edges.push({ from: parent, to: nd });
    });
  }

  const depthCount = placed.reduce((m, p) => Math.max(m, p.depth + 1), 0);
  const maxOffset = placed.reduce((m, p) => Math.max(m, p.offset), 0);
  // Emitted in post-order above; hand back list order so the DOM order (and therefore
  // tab order) still follows the sequence the user sees in the Table view.
  placed.sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0));
  return { nodes: placed, depthCount, maxOffset, edges };
}

/** Manual zoom range. Wider than auto-fit ever goes — this is the user's to drive. */
export const ZOOM_MIN = 0.3;
export const ZOOM_MAX = 2;

/**
 * Legibility floor for AUTO-fit only.
 *
 * Auto-fit stops shrinking here even if the tree still overflows: below roughly half
 * size a node card stops being readable and the canvas becomes a diagram of itself.
 * When the fit is floored the caller turns on pan/scroll instead — graceful degradation
 * rather than an unreadable postage stamp. Manual zoom can still go below it on purpose.
 */
export const MIN_FIT_SCALE = 0.5;

/** Slack inside the viewport so the tree never touches the edge or a scrollbar. */
const FIT_PADDING = 8;

export const clampZoom = (z) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z));

/**
 * The scale that fits the WHOLE bounding box inside the viewport.
 *
 * Both axes, not just width — fitting width alone is what let a fan-out run off the
 * bottom. Capped at 1 so a two-step suite is shown at natural size rather than blown
 * up to fill the panel, and floored at MIN_FIT_SCALE for legibility.
 *
 * `floored` tells the caller the tree could NOT be fitted at a readable size, which is
 * the signal to enable panning.
 */
export const fitView = (viewport, bounds) => {
  if (!viewport?.width || !viewport?.height || !bounds?.width || !bounds?.height) {
    return { scale: 1, floored: false };
  }
  const w = Math.max(1, viewport.width - FIT_PADDING * 2);
  const h = Math.max(1, viewport.height - FIT_PADDING * 2);
  const raw = Math.min(w / bounds.width, h / bounds.height);
  const capped = Math.min(raw, 1);
  return { scale: Math.max(MIN_FIT_SCALE, capped), floored: capped < MIN_FIT_SCALE };
};
